/**
 * ContextChipBar — renders one chip per active context item (vault doc,
 * uploaded file, document type, customise summary). Each chip surfaces an
 * X to clear that single piece of context.
 */
import React from 'react';
import { Folder, FileText, ClipboardCheck, SlidersHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/utils';

function Chip({ icon: Icon, iconColor, label, onRemove, testId }) {
  return (
    <span
      className="inline-flex items-center"
      style={{
        gap: '5px',
        padding: '4px 8px 4px 10px',
        background: '#FFFFFF',
        border: '1px solid #E8E8E8',
        borderRadius: '9999px',
        fontSize: '11px',
        color: '#444444',
      }}
      data-testid={testId}
    >
      <Icon className="w-3 h-3 shrink-0" strokeWidth={1.7} style={{ color: iconColor || '#666666' }} />
      <span className="font-arabic max-w-[180px] truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="hover:text-black"
        style={{ marginInlineStart: '4px', color: '#999999', cursor: 'pointer' }}
        aria-label="remove"
        data-testid={`${testId}-remove`}
      >
        <X className="w-3 h-3" strokeWidth={2} />
      </button>
    </span>
  );
}

export function ContextChipBar({ ctx, isArabic }) {
  if (!ctx.hasActiveContext) return null;
  const settingsSummary = (() => {
    const parts = [];
    if (ctx.settings.language     !== 'arabic')       parts.push(ctx.settings.language);
    if (ctx.settings.detail       !== 'professional') parts.push(ctx.settings.detail);
    if (ctx.settings.tone         !== 'formal')       parts.push(ctx.settings.tone);
    if (ctx.settings.jurisdiction !== 'saudi')        parts.push(ctx.settings.jurisdiction);
    return parts.join(' · ');
  })();

  return (
    <div
      className={cn('flex flex-wrap', isArabic && 'flex-row-reverse')}
      style={{ gap: '6px', padding: '8px 0 4px', marginBottom: '4px' }}
      data-testid="context-chip-bar"
    >
      {ctx.vaultDocuments.map((d) => (
        <Chip
          key={`v-${d.id}`}
          icon={Folder}
          label={d.name}
          onRemove={() => ctx.removeVaultDoc(d.id)}
          testId={`ctx-chip-vault-${d.id}`}
        />
      ))}
      {ctx.uploadedFile && (
        <Chip
          icon={FileText}
          iconColor={ctx.uploadedFile.error ? '#B91C1C' : (ctx.uploadedFile.extracting ? '#999' : '#E53E3E')}
          label={
            ctx.uploadedFile.error
              ? `${ctx.uploadedFile.name}${isArabic ? ' — تعذر القراءة' : ' — read failed'}`
              : ctx.uploadedFile.extracting
                ? `${ctx.uploadedFile.name}${isArabic ? ' — جارٍ القراءة…' : ' — reading…'}`
                : ctx.uploadedFile.name
          }
          onRemove={() => ctx.setUploadedFile(null)}
          testId="ctx-chip-file"
        />
      )}
      {ctx.documentType && (
        <Chip
          icon={ClipboardCheck}
          label={isArabic ? ctx.documentType.label : ctx.documentType.en}
          onRemove={() => ctx.setDocumentType(null)}
          testId="ctx-chip-doc-type"
        />
      )}
      {ctx.hasCustomSettings && (
        <Chip
          icon={SlidersHorizontal}
          label={settingsSummary || (isArabic ? 'إعدادات مخصصة' : 'Custom settings')}
          onRemove={ctx.resetSettings}
          testId="ctx-chip-settings"
        />
      )}
    </div>
  );
}

export default ContextChipBar;
